"use client";

import { env } from "@/env";
import { useInfiniteQuery } from "@tanstack/react-query";
import ScriptPreview, {
  ScriptPreviewSkeleton,
} from "@/components/script/script-preview";
import Particles from "@/components/home/particles";
import { FavoritedScripts } from "@/types/script";
import InfiniteScroll from "react-infinite-scroll-component";
import { useAuth } from "@/context/AuthContext";

const ITEMS_PER_PAGE = 8;

export default function FavoritesList() {
  const { accessToken } = useAuth();

  const fetchFavorites = async ({ pageParam = 1 }) => {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_URL}/user/my-favorites?page=${pageParam}&limit=${ITEMS_PER_PAGE}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    if (!response.ok) throw new Error("Failed to fetch favorites");
    return response.json() as Promise<FavoritedScripts>;
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["favorites"],
    queryFn: fetchFavorites,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.data.favorites.hasNextPage) {
        return pages.length + 1;
      }
      return undefined;
    },
  });

  const favorites =
    data?.pages.flatMap((page) =>
      page.data.favorites.result.map((fav) => fav.post),
    ) ?? [];

  return (
    <main>
      <section className="lg:pt-18 pt-24">
        {/* Gradients */}
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-[100px] sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary via-[#98c0ef] to-secondary opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div
          className="absolute inset-x-0 -z-10 transform-gpu overflow-hidden blur-[100px]"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-primary to-[#98c0ef] opacity-10 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <Particles
          className="absolute inset-0 -z-10"
          quantity={100}
          ease={80}
          refresh
        />
        <div className="z-10 mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-6 py-20 lg:px-8 lg:py-32">
          <div className="max-w-2xl items-center justify-center text-center">
            <h1 className="font-heading text-gradient mt-2 text-balance pb-1 text-4xl font-semibold tracking-tight lg:text-5xl">
              Favorite Scripts
            </h1>
            <p className="mt-4 text-balance text-lg leading-tight text-muted-foreground">
              Find scripts you&apos;ve liked in the past or are currently
              interested in.
            </p>
          </div>

          {isLoading ? (
            <div className="mt-12 min-h-[600px] w-full">
              <div className="grid w-full grid-cols-1 gap-x-1.5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <ScriptPreviewSkeleton key={`initial-loading-${index}`} />
                ))}
              </div>
            </div>
          ) : favorites.length > 0 ? (
            <div className="min-h-[600px] w-full">
              <InfiniteScroll
                dataLength={favorites.length}
                next={fetchNextPage}
                hasMore={!!hasNextPage}
                loader={
                  <div className="grid w-full grid-cols-1 gap-x-1.5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                      <ScriptPreviewSkeleton
                        key={`infinite-loading-${index}`}
                      />
                    ))}
                  </div>
                }
                className="w-full"
              >
                <div className="mt-12 grid w-full grid-cols-1 gap-x-1.5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  {favorites.map((script, index) => (
                    <ScriptPreview
                      index={index}
                      key={script.slug}
                      {...script}
                    />
                  ))}

                  {isFetchingNextPage &&
                    Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                      <ScriptPreviewSkeleton
                        key={`next-page-loading-${index}`}
                      />
                    ))}
                </div>
              </InfiniteScroll>
            </div>
          ) : (
            <div className="mt-12 flex min-h-[600px] w-full items-center justify-center rounded-md border border-white/5 bg-white/10 backdrop-blur">
              <span className="text-muted-foreground">
                No favorite scripts found.
              </span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
